import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import type { AuditEntity, AuditEntry } from '../api';
import { usePlainLanguage } from '../plainLanguage';
import {
  Button,
  Empty,
  ErrorBanner,
  List,
  ListRow,
  PageHead,
  formatDate,
  useAction,
  useLoad,
} from '../ui';

const SHOW: { value: '' | AuditEntity; label: string }[] = [
  { value: '', label: 'Everything' },
  { value: 'organization', label: 'Organizations' },
  { value: 'outlet', label: 'Outlets' },
  // Key issue, revoke and pairing are recorded against their counter.
  { value: 'counter', label: 'Counters, keys and pairing' },
  { value: 'user', label: 'Logins' },
  { value: 'provider_config', label: 'Payment providers' },
];

/** The same filter in a merchant's words, without the organization they cannot change. */
const SHOW_PLAIN: typeof SHOW = [
  { value: '', label: 'Everything' },
  { value: 'outlet', label: 'Shops' },
  { value: 'counter', label: 'Counters' },
  { value: 'user', label: 'Logins' },
  { value: 'provider_config', label: 'Bank connection' },
];

/**
 * Every configuration change across all customers, plus the staff-level ones that belong
 * to no organization. Staff only; a customer reads its own history on its organization's
 * Activity tab.
 */
export function Activity() {
  return (
    <>
      <PageHead
        title="Activity"
        hint="Every configuration change, newest first: who made it and when. Payments are not here - they live on each counter."
        actions={
          <Link to="/activity/webhooks" className="font-medium text-accent no-underline hover:underline">
            Webhook alerts
          </Link>
        }
      />
      <ActivityFeed showOrganization />
    </>
  );
}

/**
 * The change history, newest first, one page at a time. With `orgId` it is that
 * organization's; without it (staff only) it is everything. Scoping is the server's job -
 * a customer login gets its own organization whatever this asks for.
 */
export function ActivityFeed({
  orgId,
  showOrganization = false,
}: {
  orgId?: string;
  showOrganization?: boolean;
}) {
  const [entity, setEntity] = useState<'' | AuditEntity>('');
  const plain = usePlainLanguage();
  const first = useLoad(
    () => api.audit.list({ orgId, entity: entity || undefined }),
    [orgId, entity],
  );

  // Older pages are appended below the first; a new filter starts over.
  const [older, setOlder] = useState<AuditEntry[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  useEffect(() => {
    setOlder([]);
    setNextBefore(first.data?.nextBefore ?? null);
  }, [first.data]);

  const more = useAction();
  async function loadOlder() {
    if (!nextBefore) return;
    await more.run(async () => {
      const page = await api.audit.list({ orgId, entity: entity || undefined, before: nextBefore });
      setOlder((previous) => [...previous, ...page.entries]);
      setNextBefore(page.nextBefore);
    });
  }

  const entries = [...(first.data?.entries ?? []), ...older];

  return (
    <>
      {/* A row of its own rather than a card header, so the filter keeps its full width on
          a phone instead of being squeezed beside a title. */}
      <label className="mb-3 flex items-center gap-2 text-muted max-sm:flex-col max-sm:items-stretch">
        Show
        <select value={entity} onChange={(event) => setEntity(event.target.value as '' | AuditEntity)}>
          {(plain ? SHOW_PLAIN : SHOW).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <ErrorBanner message={first.error ?? more.error} />

      {first.loading && entries.length === 0 ? (
        <Empty>Loading…</Empty>
      ) : entries.length === 0 ? (
        <Empty>No changes recorded{entity ? ' of this kind' : ''} yet.</Empty>
      ) : (
        // One row per change, the same list the outlets and organizations use. A table
        // needed four columns side by side, which on a phone meant scrolling sideways to
        // find out who did something; ListRow wraps the time under the change instead.
        <List>
          {entries.map((entry) => {
            const { title, note } = describeEntry(entry, plain);
            const who = [
              describeActor(entry),
              showOrganization ? (entry.orgName ?? 'Sorting Company') : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <ListRow
                key={entry.id}
                title={<span className="font-medium">{title}</span>}
                subtitle={
                  <>
                    {note && <span className="block">{note}</span>}
                    <span className="block">{who}</span>
                  </>
                }
                actions={
                  <time dateTime={entry.at} className="whitespace-nowrap text-[12.5px] text-muted">
                    {formatDate(entry.at)}
                  </time>
                }
              />
            );
          })}
        </List>
      )}

      {nextBefore && (
        <Button
          variant="ghost"
          className="max-sm:w-full"
          disabled={more.busy}
          onClick={loadOlder}
        >
          {more.busy ? 'Loading…' : 'Load older'}
        </Button>
      )}
    </>
  );
}

function describeActor(entry: AuditEntry): string {
  if (entry.actor) return entry.actor.email;
  // Only one path writes without a login: the companion claiming a pairing code.
  return entry.action === 'counter.paired' ? 'The counter itself' : 'System';
}

/**
 * A recorded action in words. The raw action name is the fallback, so an action added
 * later still shows as something rather than nothing. `plain` is a merchant reading it
 * (plainLanguage.tsx): shops rather than outlets, PCs connected rather than keys, and no
 * key or MPOS ids.
 */
export function describeEntry(
  entry: AuditEntry,
  plain = false,
): { title: string; note?: string } {
  const d = entry.detail ?? {};
  const text = (key: string) => (typeof d[key] === 'string' ? (d[key] as string) : undefined);
  const named = (kind: string) => (text('name') ? `${kind} “${text('name')}”` : kind);
  const [, verb] = entry.action.split('.');
  const outletWord = plain ? 'Shop' : 'Outlet';

  switch (entry.action) {
    case 'organization.created':
      return { title: `${named('Organization')} created` };
    case 'organization.archived':
    case 'organization.restored':
      return {
        title: `Organization ${verb}`,
        note:
          typeof d.outlets === 'number'
            ? `with ${d.outlets} ${plain ? 'shop' : 'outlet'}(s)`
            : undefined,
      };

    case 'outlet.created':
      return {
        title: `${named(outletWord)} created`,
        note: text('mposShopId') && !plain ? `MPOS shop ${text('mposShopId')}` : undefined,
      };
    case 'counter.created':
      return {
        title: `${named('Counter')} created`,
        note: text('mposCounterId') && !plain ? `MPOS counter ${text('mposCounterId')}` : undefined,
      };
    case 'outlet.updated':
    case 'counter.updated':
      // The fields are column names (mposShopId, timezone), so a merchant gets the fact only.
      return plain
        ? { title: `${entry.entity === 'outlet' ? 'Shop' : 'Counter'} details changed` }
        : { title: `${entry.entity === 'outlet' ? 'Outlet' : 'Counter'} changed`, note: changed(d) };
    case 'outlet.archived':
    case 'outlet.restored':
      return { title: `${named(outletWord)} ${verb}` };
    case 'counter.archived':
    case 'counter.restored':
      return { title: `${named('Counter')} ${verb}` };

    case 'pairing_code.created':
      return { title: plain ? 'Connection code made for a counter' : 'Pairing code issued for a counter' };
    case 'counter.paired':
      return plain
        ? { title: 'A PC connected to a counter', note: text('machineName') }
        : {
            title: 'Counter paired',
            note: [
              text('machineName') && `from ${text('machineName')}`,
              text('keyId') && `key ${text('keyId')}`,
            ]
              .filter(Boolean)
              .join(' · '),
          };
    case 'counter_key.issued':
      return plain
        ? { title: 'A PC set up for a counter without a code' }
        : {
            title: 'Counter key issued',
            note: [text('keyId') && `key ${text('keyId')}`, text('label')].filter(Boolean).join(' · '),
          };
    case 'counter_key.revoked':
      return plain
        ? { title: 'A PC disconnected from a counter' }
        : { title: 'Counter key revoked', note: text('keyId') ? `key ${text('keyId')}` : undefined };

    case 'user.created':
      return {
        title: `Login ${text('email') ?? ''} added`.replace('  ', ' '),
        note: text('role') ? roleName(text('role')!) : undefined,
      };
    case 'user.archived':
    case 'user.restored':
      return {
        title: `Login ${text('email') ?? ''} ${verb}`.replace('  ', ' '),
        note: signedOut(d.sessionsEnded),
      };
    case 'user.password_changed':
      return {
        title: `Login ${text('email') ?? ''} ${
          d.afterReset === true ? 'chose a new password after a reset' : 'changed its password'
        }`.replace('  ', ' '),
        note: signedOut(d.otherSessionsEnded, 'other '),
      };
    case 'user.password_reset':
      return {
        title: `Password reset for ${text('email') ?? 'a login'}`,
        note: signedOut(d.sessionsEnded),
      };

    case 'provider_config.created':
    case 'provider_config.updated': {
      const provider = providerName(text('providerCode'));
      const notes = plain
        ? [
            d.credentialsChanged === true ? 'API key changed' : undefined,
            typeof d.enabled === 'boolean' ? (d.enabled ? 'turned on' : 'turned off') : undefined,
          ].filter(Boolean)
        : [
            d.credentialsChanged === true ? 'credentials changed' : undefined,
            typeof d.enabled === 'boolean' ? (d.enabled ? 'enabled' : 'disabled') : undefined,
            text('environment') === 'uat' ? 'UAT' : undefined,
          ].filter(Boolean);
      return {
        title: `${provider} ${entry.action.endsWith('created') ? 'set up' : 'settings changed'}`,
        note: notes.join(' · ') || undefined,
      };
    }
    case 'provider_config.archived':
    case 'provider_config.restored':
      return { title: `${providerName(text('providerCode'))} ${verb}` };

    case 'webhook_failure.acknowledged':
      return {
        title: 'Webhook alerts marked as seen',
        note:
          typeof d.count === 'number' ? `${d.count} failure${d.count === 1 ? '' : 's'}` : undefined,
      };

    default:
      return { title: entry.action };
  }
}

function changed(detail: Record<string, unknown>): string | undefined {
  const keys = Object.keys(detail);
  return keys.length ? `changed: ${keys.join(', ')}` : undefined;
}

/** How many sessions an archive or password change signed out; nothing when none were. */
function signedOut(count: unknown, which = ''): string | undefined {
  if (typeof count !== 'number' || count < 1) return undefined;
  return `${count} ${which}session${count === 1 ? '' : 's'} signed out`;
}

export function providerName(code: string | undefined): string {
  if (code === 'bml') return 'BML Connect';
  if (code === 'swipe') return 'Swipe';
  return code ?? 'Payment provider';
}

function roleName(role: string): string {
  if (role === 'staff') return 'Sorting Company staff';
  if (role === 'customer_admin') return 'administrator';
  return role;
}
